import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Breadcrumbs, Button, Link, Typography } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { OrderStatus } from "client/api-v2";
import type { OrderDto } from "client/api-v2";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { PageContent, pageContentMaxWidth } from "components/PageContent/PageContent";
import { PageHeaderBar } from "components/PageHeaderBar/PageHeaderBar";
import { BuilderLayout } from "areas/orders/builder/BuilderLayout";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import { isOrderLocked } from "areas/orders/builder/LockedOrderBanner";
import { formatInstantInUtc, OrderVersionHistory } from "areas/orders/components/OrderVersionHistory";
import { LicenceGlyph } from "areas/orders/components/chrome/LicenceGlyph";
import { OrderStatusChip } from "areas/orders/components/chrome/OrderStatusChip";
import { OrderRecordCard } from "areas/orders/detail/OrderRecordCard";
import { orderRoutes } from "areas/orders/order-routes";
import { getOrder, getOrderHistory } from "areas/orders/services/orderService";

function getValueOrFallbackFor(odNumber?: string) {
  return odNumber || "UNKNOWN_ORDER";
}

function HistoryBreadcrumbs({ odNumber }: { odNumber: string }) {
  return (
    <Breadcrumbs sx={{ pb: 2, typography: "body2" }}>
      <Link component={RouterLink} to={orderRoutes.root}>
        Orders
      </Link>
      <Typography variant="body2" color="text.secondary">
        {odNumber}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        history
      </Typography>
    </Breadcrumbs>
  );
}

function ImmutableRecordAlert({ acceptedAt }: { acceptedAt: string | null }) {
  const when = acceptedAt === null ? "—" : `${formatInstantInUtc(acceptedAt)} UTC`;

  return (
    <Alert severity="success">{`Immutable. Accepted ${when} — invoice status is tracked on the invoice, not here.`}</Alert>
  );
}

function openOrderLabel(order: OrderDto): string {
  return isOrderLocked(order.status) ? "View order" : "Open in builder";
}

export function OrderVersionHistoryPage() {
  const { odNumber } = useParams<{ odNumber: string }>();
  const resolvedOdNumber = getValueOrFallbackFor(odNumber);

  const { data: order, loading: orderLoading, error: orderError } = useAsyncRequest(getOrder, resolvedOdNumber);
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
  } = useAsyncRequest(getOrderHistory, resolvedOdNumber);

  return (
    <LoadablePage loading={(orderLoading && !order) || (historyLoading && !history)} error={orderError || historyError}>
      {order && (
        <>
          <PageHeaderBar maxWidth={pageContentMaxWidth}>
            <HistoryBreadcrumbs odNumber={order.odNumber} />
            <DetailsPageLayout
              pageName={order.odNumber}
              pageNameSx={{ fontWeight: 500 }}
              titleIcon={<LicenceGlyph odNumber={order.odNumber} />}
              titleBadge={<OrderStatusChip status={order.status} />}
              actions={[
                <Button
                  key="open"
                  variant="outlined"
                  component={RouterLink}
                  to={orderRoutes.detail(order.odNumber).root}
                >
                  {openOrderLabel(order)}
                </Button>,
              ]}
            />
          </PageHeaderBar>
          <PageContent maxWidth={pageContentMaxWidth}>
            <BuilderLayout
              rail={
                <>
                  <OrderRecordCard order={order} />
                  {order.status === OrderStatus.Accepted && <ImmutableRecordAlert acceptedAt={order.acceptedAt} />}
                </>
              }
            >
              <BuilderSection title="Version history">
                <OrderVersionHistory history={history ?? []} />
              </BuilderSection>
            </BuilderLayout>
          </PageContent>
        </>
      )}
    </LoadablePage>
  );
}
